/**
 * =============================================================================
 * AffiliatesTableView — Tabela reutilizável de Afiliados (módulo Indicação)
 *
 * Padronizado 1:1 com `CommissionsTableView` e `CrmDeals`:
 *  - Tabela full-bleed (sem moldura `rounded-lg border` externa).
 *  - Barra lateral colorida (8px) por status no início da linha.
 *  - Headers compactos com ordenação local da página atual.
 *  - Badges densos `text-[10px]`, células `text-sm`/`text-xs`.
 *  - Skeleton e estado vazio também full-bleed.
 *
 * @version 1.1.0
 * RELEASE:
 *   2026-04-25 v1.0.0 - Criação para padronização da página Afiliados.
 *   2026-04-25 v1.1.0 - Adicionadas 6 colunas: Cliques, Pontos, Total Ganho,
 *                       Total Pago, Último Login e ação rápida "Copiar link
 *                       de divulgação". Tipo da prop migrado para
 *                       `PaginatedAffiliate` (superset compatível).
 * =============================================================================
 */
import React, { useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Users,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Link2,
  Layers,
  Check,
  X,
  Pause,
  RotateCcw,
  Loader2,
  Plus,
} from 'lucide-react';
import { useAffiliateMembershipActions } from '@/hooks/useAffiliateMembershipActions';
// v2.2.0: modal de cadastro reaproveitado em modo "vincular a programa"
import { AffiliateAdminCreateModal } from '@/components/referral/AffiliateAdminCreateModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import {
  AffiliateStatusBadge,
  AFFILIATE_STATUS_CONFIG,
} from './AffiliateStatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getPresenceState } from '@/lib/affiliate-presence';
import { StatusBar } from '@/components/referral/shared/StatusBar';
import { toast } from 'sonner';
import type { AffiliateStatus } from '@/hooks/useReferralAffiliates';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import type {
  PaginatedAffiliate,
  AffiliateMembership,
} from '@/hooks/useReferralAffiliatesPaginated';

// ============================================================================
// CONSTANTES E MAPEAMENTOS
// ============================================================================

// Mapa de status migrado para `AffiliateStatusBadge` (fonte única de
// verdade para label/cor de status de afiliado/membership). Cor da barra
// lateral é derivada de `AFFILIATE_STATUS_CONFIG` em `getStatusBarColor`.

/** Cor da barra lateral por status — derivada de `AFFILIATE_STATUS_CONFIG`. */
function getStatusBarColor(status: AffiliateStatus): string {
  return AFFILIATE_STATUS_CONFIG[status]?.bar ?? 'bg-muted-foreground/40';
}

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Campos suportados pela ordenação local. */
type SortField =
  | 'full_name'
  | 'status'
  | 'total_referrals'
  | 'total_conversions'
  | 'total_clicks'
  | 'available_balance'
  | 'total_sales'
  | 'total_earned'
  | 'total_paid'
  | 'last_login_at'
  | 'last_seen_at'
  | 'created_at';

type SortDirection = 'asc' | 'desc';

// ============================================================================
// COMPONENTE: CÉLULA DE PROGRAMAS VINCULADOS (v2.2.0)
// ----------------------------------------------------------------------------
// Renderiza badges com os nomes dos programas que o afiliado participa.
// Regras:
//   - 0 adesões → "—" muted.
//   - 1 ou 2 adesões → badge(s) com nome do programa (truncado) + bullet de status.
//   - 3+ adesões → primeira badge + chip "+N" com tooltip listando todos.
// ============================================================================
function ProgramsCell({ memberships }: { memberships: AffiliateMembership[] }) {
  if (!memberships || memberships.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const renderBadge = (m: AffiliateMembership, key: string) => {
    const dotClass =
      AFFILIATE_STATUS_CONFIG[m.status as AffiliateStatus]?.dot ??
      'bg-muted-foreground/40';
    const statusLabel =
      AFFILIATE_STATUS_CONFIG[m.status as AffiliateStatus]?.label ?? m.status;
    return (
      <Tooltip key={key}>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="text-[10px] h-5 px-1.5 max-w-full gap-1 font-normal cursor-help"
          >
            <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotClass)} />
            <span className="truncate">{m.program_name ?? '—'}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-xs space-y-0.5">
            <div className="font-medium">{m.program_name ?? '—'}</div>
            {m.program_slug && (
              <div className="font-mono text-[10px] opacity-70">/{m.program_slug}</div>
            )}
            <div className="opacity-80">Status: {statusLabel}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  if (memberships.length <= 2) {
    return (
      <div className="flex flex-wrap items-center gap-1 min-w-0">
        {memberships.map((m, i) => renderBadge(m, m.id ?? String(i)))}
      </div>
    );
  }

  const first = memberships[0];
  const rest = memberships.slice(1);
  return (
    <div className="flex items-center gap-1 min-w-0">
      {renderBadge(first, first.id ?? '0')}
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="text-[10px] h-5 px-1.5 shrink-0 cursor-help">
            +{rest.length}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-xs space-y-1 max-w-[240px]">
            <div className="font-medium opacity-80">Demais programas:</div>
            {rest.map((m) => {
              const label =
                AFFILIATE_STATUS_CONFIG[m.status as AffiliateStatus]?.label ?? m.status;
              return (
                <div key={m.id} className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full shrink-0',
                      AFFILIATE_STATUS_CONFIG[m.status as AffiliateStatus]?.dot ??
                        'bg-muted-foreground/40',
                    )}
                  />
                  <span className="truncate">{m.program_name ?? '—'}</span>
                  <span className="opacity-60 text-[10px]">· {label}</span>
                </div>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL
// ============================================================================


function SortableHeader({
  field,
  label,
  currentField,
  direction,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  // [PATCH 2026-05-03] currentField pode ser null quando a ordenação local
  // está desligada (lista vem ordenada pelo backend).
  currentField: SortField | null;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;
  return (
    <TableHead
      className={cn(
        'text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none',
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center',
      )}
      onClick={() => onSort(field)}
    >
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// COMPONENTE: SUB-TABELA DE MEMBERSHIPS (expand)
// v2.0.0 — Modelo N:N (Uma pessoa, vários programas)
// Renderiza uma "linha-filha" abaixo da linha-mãe contendo todas as adesões
// (memberships) do afiliado, com programa, status, totais e ações por adesão.
// ============================================================================

function MembershipsExpandRow({
  affiliate,
  colSpan,
}: {
  affiliate: PaginatedAffiliate;
  colSpan: number;
}) {
  const memberships = affiliate.memberships ?? [];

  // v2.2.0: controla o modal de vínculo a novo programa
  const [linkOpen, setLinkOpen] = useState(false);

  // Hook de ações por adesão (aprovar, rejeitar, suspender, reativar)
  const {
    approveMembership,
    rejectMembership,
    suspendMembership,
    reactivateMembership,
    isLoading: isMutating,
  } = useAffiliateMembershipActions();

  // Estado do diálogo de confirmação para ações destrutivas (rejeitar/suspender)
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    action: 'reject' | 'suspend' | null;
    membershipId: string | null;
    programName: string | null;
  }>({ open: false, action: null, membershipId: null, programName: null });

  // Abre o diálogo de confirmação para uma ação destrutiva específica
  const openConfirm = (
    action: 'reject' | 'suspend',
    membershipId: string,
    programName: string | null,
  ) => {
    setConfirmState({ open: true, action, membershipId, programName });
  };

  // Executa a ação confirmada e fecha o diálogo
  const handleConfirm = async () => {
    if (!confirmState.membershipId || !confirmState.action) return;
    if (confirmState.action === 'reject') {
      await rejectMembership(confirmState.membershipId);
    } else {
      await suspendMembership(confirmState.membershipId);
    }
    setConfirmState({ open: false, action: null, membershipId: null, programName: null });
  };

  // Texto dinâmico do diálogo conforme a ação
  const confirmCopy =
    confirmState.action === 'reject'
      ? {
          title: 'Rejeitar adesão?',
          description: `Esta ação rejeitará a adesão de ${affiliate.full_name ?? 'o afiliado'} no programa "${confirmState.programName ?? '—'}".`,
          confirmText: 'Rejeitar',
        }
      : {
          title: 'Suspender adesão?',
          description: `A adesão de ${affiliate.full_name ?? 'o afiliado'} no programa "${confirmState.programName ?? '—'}" será suspensa.`,
          confirmText: 'Suspender',
        };

  return (
    <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
      <TableCell colSpan={colSpan} className="p-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Adesões a programas
            </span>
            <Badge
              variant="secondary"
              className="text-[10px] h-4 px-1.5"
            >
              {memberships.length}
            </Badge>

            {/* v2.2.0: vincular esta pessoa a outro programa de indicação */}
            <Button
              size="sm"
              variant="outline"
              className="h-6 gap-1 text-[11px] ml-auto"
              onClick={() => setLinkOpen(true)}
            >
              <Plus className="h-3 w-3" />
              Adicionar a programa
            </Button>
          </div>

          <AffiliateAdminCreateModal
            open={linkOpen}
            onOpenChange={setLinkOpen}
            affiliate={{
              id: affiliate.id,
              full_name: affiliate.full_name,
              email: affiliate.email,
              phone: null,
              document_number: null,
              document_type: null,
              programIds: memberships
                .map((m) => m.program_id)
                .filter(Boolean) as string[],
            }}
          />

          {memberships.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              Nenhuma adesão registrada para este afiliado.
            </p>
          ) : (
            <div className="rounded-md border bg-background overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground">
                      Programa
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground">
                      Status
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      Indic.
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      Conv.
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      T. Ganho
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      T. Pago
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      Saldo
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground">
                      Aprovação
                    </th>
                    <th className="px-3 py-2 font-medium text-[10px] uppercase tracking-wide text-muted-foreground text-right">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((m: AffiliateMembership) => {
                    // Define quais ações estão disponíveis conforme status atual
                    const canApprove = m.status === 'pending';
                    const canReject = m.status === 'pending';
                    const canSuspend = m.status === 'active';
                    const canReactivate =
                      m.status === 'suspended' || m.status === 'rejected';

                    return (
                      <tr
                        key={m.id}
                        className="border-t border-border/40 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground truncate max-w-[220px]">
                            {m.program_name ?? '—'}
                          </div>
                          {m.program_slug && (
                            <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[220px]">
                              {m.program_slug}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {/* Status do membership — padrão "dot + texto" */}
                          <AffiliateStatusBadge status={m.status} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {m.total_referrals ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {m.total_conversions ?? 0}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {formatBRL(m.total_earned)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {formatBRL(m.total_paid)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {formatBRL(m.available_balance)}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-muted-foreground">
                          {m.approved_at
                            ? format(new Date(m.approved_at), 'dd/MM/yy', {
                                locale: ptBR,
                              })
                            : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {/* Botões de ação por adesão — visíveis conforme status */}
                          <div className="flex items-center justify-end gap-1">
                            {canApprove && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon-sm"
                                    variant="ghost"
                                    disabled={isMutating}
                                    onClick={() => approveMembership(m.id)}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                                  >
                                    {isMutating ? (
                                      <Loader2 className="animate-spin" />
                                    ) : (
                                      <Check />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Aprovar adesão</TooltipContent>
                              </Tooltip>
                            )}
                            {canReject && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon-sm"
                                    variant="ghost"
                                    disabled={isMutating}
                                    onClick={() =>
                                      openConfirm('reject', m.id, m.program_name)
                                    }
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  >
                                    <X />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Rejeitar adesão</TooltipContent>
                              </Tooltip>
                            )}
                            {canSuspend && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon-sm"
                                    variant="ghost"
                                    disabled={isMutating}
                                    onClick={() =>
                                      openConfirm('suspend', m.id, m.program_name)
                                    }
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
                                  >
                                    <Pause />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Suspender adesão</TooltipContent>
                              </Tooltip>
                            )}
                            {canReactivate && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon-sm"
                                    variant="ghost"
                                    disabled={isMutating}
                                    onClick={() => reactivateMembership(m.id)}
                                    className="text-primary hover:bg-primary/10"
                                  >
                                    {isMutating ? (
                                      <Loader2 className="animate-spin" />
                                    ) : (
                                      <RotateCcw />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Reativar adesão</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </TableCell>

      {/* Diálogo de confirmação para ações destrutivas (rejeitar/suspender) */}
      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={(open) =>
          setConfirmState((prev) => ({ ...prev, open }))
        }
        variant={confirmState.action === 'reject' ? 'danger' : 'warning'}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmText={confirmCopy.confirmText}
        onConfirm={handleConfirm}
        isLoading={isMutating}
      />
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

function AffiliateTableRow({
  affiliate,
  isExpanded,
  onToggleExpand,
  onOpenDetails,
  totalColumns,
}: {
  affiliate: PaginatedAffiliate;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onOpenDetails: (id: string) => void;
  totalColumns: number;
}) {
  // Status visual agora é renderizado pelo `AffiliateStatusBadge` (dot + texto).

  // ----------------------------------------------------------------
  // v1.2.0 — Modelo campanha-driven (Bloco 5H)
  // O afiliado pode ter:
  //   (a) `personal_slug` definido (modelo legado) → copia /r/<slug>
  //   (b) apenas links provisionados por campanha (active_links_count > 0)
  //       → abre o drawer de detalhes (cada campanha tem URL própria)
  // O ícone só fica desabilitado quando NENHUMA das duas condições é true.
  // ----------------------------------------------------------------
  const hasPersonalSlug = !!affiliate.personal_slug;
  const hasCampaignLinks = (affiliate.active_links_count ?? 0) > 0;
  const hasLink = hasPersonalSlug || hasCampaignLinks;

  // v2.0.0 — habilitar chevron apenas quando há mais de 1 adesão
  const programsCount = affiliate.programs_count ?? 0;
  const canExpand = programsCount > 1;

  const handleLinkAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasLink) return;

    // Caso (a) — copia o link pessoal direto
    if (hasPersonalSlug) {
      const url = `${window.location.origin.replace(/\/$/, '')}/r/${affiliate.personal_slug}`;
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success('Link copiado', { description: url }))
        .catch(() => toast.error('Não foi possível copiar o link'));
      return;
    }

    // Caso (b) — abre o drawer (links de campanha são visualizados/copiados lá)
    onOpenDetails(affiliate.id);
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canExpand) return;
    onToggleExpand(affiliate.id);
  };

  return (
    <>
      <TableRow
        className="hover:bg-muted/40 border-b border-border/40 group transition-colors cursor-pointer"
        onClick={() => onOpenDetails(affiliate.id)}
      >
        {/* Barra lateral colorida por status (helper compartilhado) */}
        <TableCell className="p-0 w-2">
          <StatusBar
            colorClass={getStatusBarColor(affiliate.status as AffiliateStatus)}
          />
        </TableCell>

        {/* Ref. — 8 primeiros dígitos do UUID + chevron de expand inline (v2.1.0)
            Chevron movido para dentro da célula Ref. para eliminar coluna extra
            de 36px que causava desalinhamento visual frente às demais listagens
            do módulo Indicação (Indicações, Comissões, Pagamentos). */}
        <TableCell className="px-2 py-1.5">
          <div className="flex items-center gap-1.5">
            {canExpand ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5 -ml-1 shrink-0"
                    onClick={handleToggleExpand}
                    aria-label={
                      isExpanded
                        ? 'Recolher adesões'
                        : `Expandir ${programsCount} adesões`
                    }
                    aria-expanded={isExpanded}
                  >
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-transform',
                        isExpanded && 'rotate-90',
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">
                    {isExpanded
                      ? 'Recolher adesões'
                      : `Ver ${programsCount} adesões`}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null}
            <span className="font-mono text-[11px] text-muted-foreground">
              {affiliate.id.substring(0, 8).toUpperCase()}
            </span>
          </div>
        </TableCell>

        {/* Afiliado (nome + email) — v2.2.0: badge de programas movido para coluna própria */}
        <TableCell className="px-2 py-1.5 max-w-[220px]">
          <div className="font-medium text-sm truncate">
            {affiliate.full_name ?? '—'}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {affiliate.email ?? ''}
          </div>
        </TableCell>

        {/* Slug pessoal */}
        <TableCell className="px-2 py-1.5 text-xs text-muted-foreground max-w-[140px]">
          <span className="truncate block font-mono">
            {affiliate.personal_slug ?? '—'}
          </span>
        </TableCell>

        {/* Programas vinculados (v2.2.0) — exibe nome(s) do(s) programa(s) que o afiliado participa */}
        <TableCell className="px-2 py-1.5 max-w-[200px]">
          <ProgramsCell memberships={affiliate.memberships ?? []} />
        </TableCell>

        {/* Status — padrão "dot + texto" (AffiliateStatusBadge) */}
        <TableCell className="px-2 py-1.5">
          <AffiliateStatusBadge status={affiliate.status} />
        </TableCell>

        {/* Indicações */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm tabular-nums">
            {affiliate.total_referrals ?? 0}
          </span>
        </TableCell>

        {/* Conversões */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm tabular-nums">
            {affiliate.total_conversions ?? 0}
          </span>
        </TableCell>

        {/* Cliques */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm tabular-nums">
            {affiliate.total_clicks ?? 0}
          </span>
        </TableCell>

        {/* T. Vendas — receita gerada para a empresa (v1.5.1 — movido para após Cliques) */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm font-medium tabular-nums">
            {formatBRL(affiliate.total_sales)}
          </span>
        </TableCell>

        {/* Total Ganho */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm tabular-nums text-muted-foreground">
            {formatBRL(affiliate.total_earned)}
          </span>
        </TableCell>

        {/* Total Pago */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm tabular-nums text-muted-foreground">
            {formatBRL(affiliate.total_paid)}
          </span>
        </TableCell>

        {/* Saldo (movido para depois de T. Pago — v1.2.0) */}
        <TableCell className="text-right px-2 py-1.5">
          <span className="text-sm font-medium tabular-nums">
            {formatBRL(affiliate.available_balance)}
          </span>
        </TableCell>

        {/* Cadastro — agora com hora/minuto (v1.2.0) */}
        <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
          {affiliate.created_at
            ? format(new Date(affiliate.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })
            : '—'}
        </TableCell>

        {/* Ult. Login — data formatada simples (v1.4.0) */}
        <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
          {affiliate.last_login_at ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="whitespace-nowrap">
                  {format(new Date(affiliate.last_login_at), 'dd/MM/yy HH:mm', {
                    locale: ptBR,
                  })}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {format(
                  new Date(affiliate.last_login_at),
                  "dd/MM/yyyy 'às' HH:mm:ss",
                  { locale: ptBR },
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            '—'
          )}
        </TableCell>

        {/* Ult. Ativi — presença online em tempo real (v1.4.0) */}
        <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
          {(() => {
            const presence = getPresenceState(affiliate.last_seen_at);
            const seenText = affiliate.last_seen_at
              ? format(new Date(affiliate.last_seen_at), "dd/MM/yy 'às' HH:mm", {
                  locale: ptBR,
                })
              : '—';
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        presence.dotClass,
                        presence.status === 'online' && 'animate-pulse',
                      )}
                    />
                    <span
                      className={cn(
                        'whitespace-nowrap',
                        presence.status === 'online' &&
                          'text-emerald-600 font-medium',
                      )}
                    >
                      {presence.label}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <span className="text-muted-foreground">Última atividade:</span>{' '}
                  {seenText}
                </TooltipContent>
              </Tooltip>
            );
          })()}
        </TableCell>

        {/* Link de Divulgação (v1.2.0 — copiar OU abrir drawer) */}
        <TableCell className="px-2 py-1.5 text-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  'h-7 w-7',
                  !hasLink && 'opacity-40 cursor-not-allowed',
                  hasLink && 'text-primary hover:text-primary',
                )}
                onClick={handleLinkAction}
                disabled={!hasLink}
                aria-label={
                  hasPersonalSlug
                    ? 'Copiar link de divulgação'
                    : hasCampaignLinks
                      ? 'Ver links de campanha'
                      : 'Afiliado sem link de divulgação'
                }
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {hasPersonalSlug
                  ? 'Copiar link de divulgação'
                  : hasCampaignLinks
                    ? `Ver ${affiliate.active_links_count} link${(affiliate.active_links_count ?? 0) > 1 ? 's' : ''} de campanha`
                    : 'Afiliado sem link de divulgação'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TableCell>

        {/* Ações */}
        <TableCell className="px-2 py-1.5 text-right">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(affiliate.id);
            }}
            title="Ver detalhes"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>

      {/* v2.0.0 — Linha-filha com sub-tabela de adesões (apenas quando expandido) */}
      {isExpanded && canExpand && (
        <MembershipsExpandRow affiliate={affiliate} colSpan={totalColumns} />
      )}
    </>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

interface AffiliatesTableViewProps {
  /** Lista de afiliados da página atual (vinda da RPC paginada). */
  affiliates: PaginatedAffiliate[];
  /** Estado de loading (skeleton). */
  isLoading: boolean;
  /** Callback ao clicar para abrir detalhes. */
  onOpenDetails: (id: string) => void;
  /** Mensagem para estado vazio. */
  emptyMessage?: string;
}

/**
 * Tabela de Afiliados — paridade visual 99,99% com `CommissionsTableView`/`CrmDeals`.
 */
export function AffiliatesTableView({
  affiliates,
  isLoading,
  onOpenDetails,
  emptyMessage = 'Nenhum afiliado encontrado',
}: AffiliatesTableViewProps) {
  // ========== ESTADOS LOCAIS ==========
  // [PATCH 2026-05-03] sortField inicial = null → respeita a ordem vinda do
  // backend (RPC `get_referral_affiliates_paginated` com sort_by='performance':
  // Indicações > Conversões > Cliques). A ordenação local só passa a valer
  // quando o usuário clica em um cabeçalho.
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // v2.0.0 — expand/collapse por linha (1 pessoa pode ter múltiplas adesões)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  /** Alterna a expansão de uma linha (mantém demais como estão). */
  const handleToggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ========== ORDENAÇÃO LOCAL (página atual) ==========
  // [PATCH 2026-05-03] Quando `sortField` é null, devolve os afiliados
  // exatamente na ordem entregue pelo backend (ordenação composta de
  // performance). Evita que o componente reordene por created_at e mascare
  // a regra "Indicações > Conversões > Cliques" definida na RPC.
  const sortedAffiliates = useMemo(() => {
    if (sortField === null) return affiliates;
    return [...affiliates].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'full_name':
          comparison = (a.full_name || '').localeCompare(b.full_name || '');
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'total_referrals':
          comparison = Number(a.total_referrals ?? 0) - Number(b.total_referrals ?? 0);
          break;
        case 'total_conversions':
          comparison = Number(a.total_conversions ?? 0) - Number(b.total_conversions ?? 0);
          break;
        case 'total_clicks':
          comparison = Number(a.total_clicks ?? 0) - Number(b.total_clicks ?? 0);
          break;
        case 'total_sales':
          comparison = Number(a.total_sales ?? 0) - Number(b.total_sales ?? 0);
          break;
        case 'available_balance':
          comparison = Number(a.available_balance ?? 0) - Number(b.available_balance ?? 0);
          break;
        case 'total_earned':
          comparison = Number(a.total_earned ?? 0) - Number(b.total_earned ?? 0);
          break;
        case 'total_paid':
          comparison = Number(a.total_paid ?? 0) - Number(b.total_paid ?? 0);
          break;
        case 'last_login_at':
          comparison =
            new Date(a.last_login_at ?? 0).getTime() -
            new Date(b.last_login_at ?? 0).getTime();
          break;
        case 'last_seen_at':
          // v1.4.0 - ordenação independente por última atividade (presença)
          comparison =
            new Date(a.last_seen_at ?? 0).getTime() -
            new Date(b.last_seen_at ?? 0).getTime();
          break;
        case 'created_at':
          comparison =
            new Date(a.created_at ?? 0).getTime() -
            new Date(b.created_at ?? 0).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [affiliates, sortField, sortDirection]);

  /** Alterna ordenação ao clicar no header. */
  // [PATCH 2026-05-03] Aceita `currentField` nulo (default = ordem do backend).
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== SKELETON LOADING ==========
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // ========== ESTADO VAZIO ==========
  if (affiliates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
          <Users className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="text-center">
          <h3 className="font-medium text-sm">{emptyMessage}</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Tente ajustar os filtros aplicados.
          </p>
        </div>
      </div>
    );
  }

  // ========== MIN-WIDTH DA TABELA ==========
  // v1.1.0: aumentado para acomodar as 6 novas colunas (cliques, pontos,
  // total ganho, total pago, último login, link).
  // v2.0.0: +36px para a nova coluna de chevron (expand/collapse).
  // v2.1.0: -36px — chevron movido para dentro da célula Ref. (alinhamento padrão).
  const minWidth = '1820px';

  // v2.2.0 — coluna "Programas" adicionada (16 → 17)
  const TOTAL_COLUMNS = 17;

  // ========== TABELA FULL-BLEED ==========
  return (
    <ScrollIndicators className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full">
      <div
        ref={scrollContainerRef}
        data-scroll-viewport
        className="flex-1 min-h-0 w-full max-w-full overflow-auto"
      >
        <table
          className="w-max caption-bottom text-sm table-fixed"
          style={{ minWidth }}
        >
          {/* Larguras de colunas */}
          <colgroup>
            <col style={{ width: 8 }} />   {/* Barra de status */}
            {/* v2.1.0: coluna dedicada do chevron removida — agora inline na célula Ref. */}
            <col className="w-[75px]" />   {/* Ref. (+ chevron inline quando aplicável) */}
            <col className="w-[220px]" />  {/* Afiliado */}
            <col className="w-[140px]" />  {/* Slug */}
            <col className="w-[200px]" />  {/* Programas (v2.2.0) */}
            <col className="w-[100px]" />  {/* Status */}
            <col className="w-[80px]" />   {/* Indicações */}
            <col className="w-[80px]" />   {/* Conversões */}
            <col className="w-[80px]" />   {/* Cliques */}
            <col className="w-[110px]" />  {/* T. Vendas (v1.5.1 — movido para após Cliques) */}
            <col className="w-[110px]" />  {/* T. Ganho */}
            <col className="w-[110px]" />  {/* T. Pago */}
            <col className="w-[110px]" />  {/* Saldo (movido após T. Pago — v1.2.0) */}
            <col className="w-[80px]" />   {/* Cadastro */}
            <col className="w-[110px]" />  {/* Últ. Login */}
            <col className="w-[50px]" />   {/* Link */}
            <col className="w-[60px]" />   {/* Ações */}
          </colgroup>

          {/* Header sticky */}
          <TableHeader className="sticky top-0 bg-background z-[1]">
            <TableRow className="border-b">
              <TableHead className="p-0" />
              {/* v2.1.0: header dedicado do chevron removido (chevron agora inline em Ref.) */}
              <TableHead className="text-xs font-medium px-2 py-1.5">Ref.</TableHead>
              <SortableHeader
                field="full_name"
                label="Afiliado"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <TableHead className="text-xs font-medium py-2">Slug</TableHead>
              <TableHead className="text-xs font-medium py-2">Programas</TableHead>
              <SortableHeader
                field="status"
                label="Status"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="total_referrals"
                label="Indic."
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="total_conversions"
                label="Conv."
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="total_clicks"
                label="Cliques"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              {/* v1.5.1 — T. Vendas movido para logo após Cliques */}
              <SortableHeader
                field="total_sales"
                label="T. Vendas"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="total_earned"
                label="T. Ganho"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="total_paid"
                label="T. Pago"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="available_balance"
                label="Saldo"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
                align="right"
              />
              <SortableHeader
                field="created_at"
                label="Cadastro"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              {/* v1.4.0 - Coluna "Atividade" desmembrada em duas colunas */}
              <SortableHeader
                field="last_login_at"
                label="Ult. Login"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                field="last_seen_at"
                label="Ult. Ativi."
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <TableHead className="text-xs font-medium py-2 text-center">
                Link
              </TableHead>
              <TableHead className="text-xs font-medium py-2 text-right">
                Ações
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedAffiliates.map((a) => (
              <AffiliateTableRow
                key={a.id}
                affiliate={a}
                isExpanded={expandedRows.has(a.id)}
                onToggleExpand={handleToggleExpand}
                onOpenDetails={onOpenDetails}
                totalColumns={TOTAL_COLUMNS}
              />
            ))}
          </TableBody>
        </table>
      </div>
    </ScrollIndicators>
  );
}
